import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import type { ImageFolderFilters, PrelisType } from "@shared/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import DateRangeSlider from "@/components/manuals/DateRangeSlider";
import { Switch } from "@/components/ui/switch";

interface ImageFiltersProps {
  filters: ImageFolderFilters;
  onFiltersChange: (filters: ImageFolderFilters) => void;
  onClose?: () => void;
  minDate?: string; // YYYY-MM format
  maxDate?: string; // YYYY-MM format
  dateFilterEnabled?: boolean;
  onDateFilterToggle?: (enabled: boolean) => void;
  optionCounts?: Partial<
    Record<"panelStructure" | "brand" | "folderType" | "model" | "color" | "prelisType", Record<string, number>>
  >;
  /** Ha true, nem jelenik meg a „Filtre” / filtersLabel sor (pl. Galéria oldal) */
  hideFiltersTitle?: boolean;
}

/** Slug for lookup value key: lowercase, spaces → underscore */
function lookupSlug(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_");
}

export default function ImageFilters({
  filters,
  onFiltersChange,
  onClose,
  minDate,
  maxDate,
  dateFilterEnabled = false,
  onDateFilterToggle,
  optionCounts,
  hideFiltersTitle = false,
}: ImageFiltersProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'sk' ? 'sk' : i18n.language === 'en' ? 'en' : 'hu';

  /** Translate lookup value if key exists in lookupValues.<type>.<slug>, else return original value */
  const getLookupLabel = (type: string, value: string): string => {
    const slug = lookupSlug(value);
    const key = `lookupValues.${type}.${slug}`;
    const translated = t(key);
    return translated !== key ? translated : value;
  };

  // Fetch lookups for each type
  const panelStructureQuery = useQuery({
    queryKey: ["image-lookups", "panel_structure", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=panel_structure&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  const brandQuery = useQuery({
    queryKey: ["image-lookups", "brand", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=brand&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  const colorQuery = useQuery({
    queryKey: ["image-lookups", "color", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=color&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  const prelisQuery = useQuery({
    queryKey: ["image-lookups", "prelis", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=prelis&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  const folderTypeQuery = useQuery({
    queryKey: ["image-lookups", "folder_type", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=folder_type&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  const modelQuery = useQuery({
    queryKey: ["image-lookups", "model", lang],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/lookups?type=model&lang=${lang}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return data.lookups as Array<{ id: string; type: string; value: string; display_order: number }>;
    },
    retry: false,
  });

  // Convert lookups to items, sorted alphabetically by name
  const panelStructureItems = useMemo(() => {
    return (panelStructureQuery.data || [])
      .map(lookup => ({
        id: lookup.value,
        name: lookup.value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [panelStructureQuery.data]);

  const brandItems = useMemo(() => {
    return (brandQuery.data || [])
      .map(lookup => ({
        id: lookup.value,
        name: lookup.value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [brandQuery.data]);

  const colorItems = useMemo(() => {
    return (colorQuery.data || [])
      .map(lookup => ({
        id: lookup.value,
        name: lookup.value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [colorQuery.data]);

  const prelisItems = useMemo(() => {
    return (prelisQuery.data || [])
      .map(lookup => ({
        id: lookup.value,
        name: lookup.value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [prelisQuery.data]);

  const folderTypeItems = useMemo(() => {
    return (folderTypeQuery.data || [])
      .map(lookup => ({ id: lookup.value, name: lookup.value }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [folderTypeQuery.data]);

  const modelItems = useMemo(() => {
    return (modelQuery.data || [])
      .map(lookup => ({ id: lookup.value, name: lookup.value }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu', { sensitivity: 'base' }));
  }, [modelQuery.data]);

  const withUnknownFromCounts = useMemo(() => {
    const ismeretlenLast = (items: Array<{ id: string; name: string }>) => {
      const rest = items.filter((i) => i.id !== "Ismeretlen");
      const unk = items.filter((i) => i.id === "Ismeretlen");
      return [...rest, ...unk];
    };
    const ensureUnknown = (
      items: Array<{ id: string; name: string }>,
      key: "panelStructure" | "brand" | "folderType" | "model" | "color" | "prelisType"
    ) => {
      const hasUnknownInCounts = typeof optionCounts?.[key]?.["Ismeretlen"] === "number";
      const hasUnknownItem = items.some((i) => i.id === "Ismeretlen");
      const next =
        hasUnknownInCounts && !hasUnknownItem
          ? [...items, { id: "Ismeretlen", name: "Ismeretlen" }]
          : items;
      return ismeretlenLast(next);
    };
    return {
      panelStructure: ensureUnknown(panelStructureItems, "panelStructure"),
      brand: ensureUnknown(brandItems, "brand"),
      folderType: ensureUnknown(folderTypeItems, "folderType"),
      model: ensureUnknown(modelItems, "model"),
      color: ensureUnknown(colorItems, "color"),
      prelisType: ensureUnknown(prelisItems, "prelisType"),
    };
  }, [optionCounts, panelStructureItems, brandItems, folderTypeItems, modelItems, colorItems, prelisItems]);

  // Convert filter values to Sets
  const panelStructureSelected = useMemo(() => {
    if (!filters.panelStructure) return new Set<string>();
    return new Set(Array.isArray(filters.panelStructure) ? filters.panelStructure : [filters.panelStructure]);
  }, [filters.panelStructure]);

  const brandSelected = useMemo(() => {
    if (!filters.brand) return new Set<string>();
    return new Set(Array.isArray(filters.brand) ? filters.brand : [filters.brand]);
  }, [filters.brand]);

  const colorSelected = useMemo(() => {
    if (!filters.color) return new Set<string>();
    return new Set(Array.isArray(filters.color) ? filters.color : [filters.color]);
  }, [filters.color]);

  const prelisSelected = useMemo(() => {
    if (!filters.prelisType) return new Set<string>();
    return new Set(Array.isArray(filters.prelisType) ? filters.prelisType : [filters.prelisType]);
  }, [filters.prelisType]);

  const folderTypeSelected = useMemo(() => {
    if (!filters.folderType) return new Set<string>();
    return new Set(Array.isArray(filters.folderType) ? filters.folderType : [filters.folderType]);
  }, [filters.folderType]);

  const modelSelected = useMemo(() => {
    if (!filters.model) return new Set<string>();
    return new Set(Array.isArray(filters.model) ? filters.model : [filters.model]);
  }, [filters.model]);

  const handleToggle = (key: keyof ImageFolderFilters, value: string) => {
    const currentSet = key === "panelStructure" ? panelStructureSelected :
                      key === "brand" ? brandSelected :
                      key === "folderType" ? folderTypeSelected :
                      key === "model" ? modelSelected :
                      key === "color" ? colorSelected :
                      prelisSelected;
    
    const newSet = new Set(currentSet);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    
    const newArray = Array.from(newSet);
    updateFilter(key, newArray.length > 0 ? newArray : undefined);
  };

  const updateFilter = (key: keyof ImageFolderFilters, value: string[] | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value && value.length > 0 ? value : undefined,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
    onDateFilterToggle?.(false);
  };

  const dateFilterActive = dateFilterEnabled && (Boolean(filters.dateFrom) || Boolean(filters.dateTo));
  const hasActiveFilters = 
    (filters.panelStructure && (Array.isArray(filters.panelStructure) ? filters.panelStructure.length > 0 : true)) ||
    (filters.brand && (Array.isArray(filters.brand) ? filters.brand.length > 0 : true)) ||
    (filters.folderType && (Array.isArray(filters.folderType) ? filters.folderType.length > 0 : true)) ||
    (filters.model && (Array.isArray(filters.model) ? filters.model.length > 0 : true)) ||
    (filters.color && (Array.isArray(filters.color) ? filters.color.length > 0 : true)) ||
    (filters.prelisType && (Array.isArray(filters.prelisType) ? filters.prelisType.length > 0 : true)) ||
    dateFilterActive;

  const renderCheckboxList = (
    title: string,
    items: Array<{ id: string; name: string }>,
    selected: Set<string>,
    onToggle: (value: string) => void,
    lookupType?: string,
    filterKey?: "panelStructure" | "brand" | "folderType" | "model" | "color" | "prelisType"
  ) => {
    return (
      <div className="space-y-2">
        <div className="text-sm font-medium">{title}</div>
        <div className="border rounded-md max-h-[96px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {t("common.noResults")}
            </div>
          ) : (
            <div className="p-1 space-y-0">
              {items.map((item) => {
                const isSelected = selected.has(item.id);
                const displayName = lookupType ? getLookupLabel(lookupType, item.name) : item.name;
                const count = filterKey ? optionCounts?.[filterKey]?.[item.id] : undefined;
                return (
                  <label
                    key={item.id}
                    className={cn(
                      'flex items-center gap-2 py-1 px-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors min-h-[2rem]',
                      isSelected && 'bg-primary/10 text-primary'
                    )}
                  >
                    <Checkbox
                      size="2xs"
                      checked={isSelected}
                      onCheckedChange={() => onToggle(item.id)}
                    />
                    <span className="text-sm flex-1 truncate">{displayName}</span>
                    {typeof count === "number" && (
                      <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {(!hideFiltersTitle || hasActiveFilters) && (
        <div
          className={cn(
            "flex items-center justify-between gap-2",
            hideFiltersTitle && "justify-end",
          )}
        >
          {!hideFiltersTitle && (
            <h3 className="text-sm font-semibold">{t("images.filtersLabel")}</h3>
          )}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t("images.delete")}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {/* Order matches table: Brand, Type, Model, Surface, Color, Prelis, Installation date */}
        {renderCheckboxList(
          t("imagesFolderExtra2.brandLabel"),
          withUnknownFromCounts.brand,
          brandSelected,
          (value) => handleToggle("brand", value),
          undefined,
          "brand"
        )}

        {renderCheckboxList(
          t("imagesFolderExtra2.typeLabel"),
          withUnknownFromCounts.folderType,
          folderTypeSelected,
          (value) => handleToggle("folderType", value),
          "folder_type",
          "folderType"
        )}

        {renderCheckboxList(
          t("imagesFolderExtra2.modelLabel"),
          withUnknownFromCounts.model,
          modelSelected,
          (value) => handleToggle("model", value),
          undefined,
          "model"
        )}

        {renderCheckboxList(
          t("imagesFolderExtra2.surfaceLabel"),
          withUnknownFromCounts.panelStructure,
          panelStructureSelected,
          (value) => handleToggle("panelStructure", value),
          undefined,
          "panelStructure"
        )}

        {renderCheckboxList(
          t("imagesFolderExtra2.colorLabel"),
          withUnknownFromCounts.color,
          colorSelected,
          (value) => handleToggle("color", value),
          undefined,
          "color"
        )}

        {renderCheckboxList(
          t("imagesFolderExtra2.prelisLabel"),
          withUnknownFromCounts.prelisType,
          prelisSelected,
          (value) => handleToggle("prelisType", value as PrelisType),
          undefined,
          "prelisType"
        )}

        {/* Szerelés napja - Date Range Slider */}
        {(minDate || maxDate) && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t("imagesExtra.installDateHead")}</div>
                <p className="text-xs text-muted-foreground">
                  {dateFilterEnabled
                    ? t("imagesExtra.dateFilterEnabledHint", "A dátumszűrő aktív.")
                    : t("imagesExtra.dateFilterDisabledHint", "A dátumszűrő ki van kapcsolva.")}
                </p>
              </div>
              <Switch
                checked={dateFilterEnabled}
                onCheckedChange={(checked) => onDateFilterToggle?.(Boolean(checked))}
              />
            </div>
            {dateFilterEnabled && (
              <DateRangeSlider
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(dateFrom, dateTo) => {
                  onFiltersChange({
                    ...filters,
                    dateFrom,
                    dateTo,
                  });
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
